import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveDynamicComponent as _resolveDynamicComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, Teleport as _Teleport, createElementVNode as _createElementVNode, createVNode as _createVNode, resolveComponent as _resolveComponent, unref as _unref, normalizeClass as _normalizeClass, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("strong", { class: "text-xl leading-8 block font-normal text-gray-500 mt-[1.3333333em]" }, "The SRV or \"service locator\" DNS record type enables service discovery in the DNS. SRV records allow services to be advertised on specific ports and used in an order controlled by the owner of the service. SRV also provides a load balancing feature.", -1)
const _hoisted_2 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The SRV record type was proposed in the late 1990s in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc2782",
    rel: "noopener noreferrer"
  }, "RFC 2782"),
  /*#__PURE__*/_createTextVNode(". The SRV record addresses limitations in the DNS around advertising and consuming services. Not all application protocols support SRV, but many do.")
], -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("figure", { class: "mt-6" }, [
  /*#__PURE__*/_createElementVNode("img", {
    class: "w-full rounded-lg",
    src: "/img/learning/dns-record-types/srv/the-dns-srv-record-type.jpg",
    alt: "Service location in the DNS",
    title: "Service location in the DNS",
    width: "1200",
    height: "600"
  }),
  /*#__PURE__*/_createElementVNode("figcaption", null, [
    /*#__PURE__*/_createTextVNode("Service location in the DNS. By NsLookup.io. Licensed under "),
    /*#__PURE__*/_createElementVNode("a", {
      href: "https://creativecommons.org/licenses/by/4.0/",
      title: "Creative Commons By 4.0 licence",
      rel: "nofollow"
    }, "CC By 4.0"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_4 = /*#__PURE__*/_createElementVNode("h2", null, "SRV record format", -1)
const _hoisted_5 = /*#__PURE__*/_createElementVNode("p", null, "Each SRV record specifies one possible endpoint for a service. Each record also includes information on how clients should select among the available endpoints. The owner name of the SRV record is an essential part of the format. Like all other records in the DNS, each SRV record also has a Time-to-Live (TTL) value in seconds.", -1)
const _hoisted_6 = /*#__PURE__*/_createElementVNode("p", null, "The SRV record format is:", -1)
const _hoisted_7 = /*#__PURE__*/_createElementVNode("pre", null, "_Service._Proto.Name TTL SRV Priority Weight Port Target", -1)
const _hoisted_8 = /*#__PURE__*/_createElementVNode("p", null, "Let's examine each of the SRV record fields one by one.", -1)
const _hoisted_9 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "_Service"),
  /*#__PURE__*/_createTextVNode(": This is a label in the DNS name that indicates the name of the service. It must begin with an underscore character.")
], -1)
const _hoisted_10 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "_Protocol"),
  /*#__PURE__*/_createTextVNode(": This is a label in the DNS name that indicates the protocol used by the service. It must begin with an underscore character. Protocol is usually either \"_tcp\" or \"_udp\".")
], -1)
const _hoisted_11 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Name"),
  /*#__PURE__*/_createTextVNode(": The DNS name of the organization or site that is providing the service.")
], -1)
const _hoisted_12 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "TTL"),
  /*#__PURE__*/_createTextVNode(": The TTL of this record in seconds. The TTL of all SRV records for a single owner name must be identical.")
], -1)
const _hoisted_13 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Priority"),
  /*#__PURE__*/_createTextVNode(": The priority of this endpoint as a 16-bit unsigned integer value. The SRV record or records with the lowest priority value among endpoints are used first.")
], -1)
const _hoisted_14 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Weight"),
  /*#__PURE__*/_createTextVNode(": A 16-bit unsigned integer value. Weights are relative. Clients use weight to select randomly among endpoints with identical priority values.")
], -1)
const _hoisted_15 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Port"),
  /*#__PURE__*/_createTextVNode(": The port number for this endpoint as a 16-bit unsigned integer.")
], -1)
const _hoisted_16 = /*#__PURE__*/_createElementVNode("strong", null, "Target", -1)
const _hoisted_17 = /*#__PURE__*/_createElementVNode("h3", null, "Priority versus weight", -1)
const _hoisted_18 = /*#__PURE__*/_createElementVNode("p", null, "The endpoints with the lowest priority are used first. Weight specifies the relative amount of requests that each endpoint with the same priority should receive.", -1)
const _hoisted_19 = /*#__PURE__*/_createElementVNode("p", null, "Clients randomly select one of the endpoints using their weights if multiple SRV records have the same priority. The client adds the weight values and generates a random number between 1 and the sum of the weights to select an endpoint.", -1)
const _hoisted_20 = /*#__PURE__*/_createElementVNode("h3", null, "An example of weights", -1)
const _hoisted_21 = /*#__PURE__*/_createElementVNode("p", null, "If two SRV records have the same priority value and weight values of 100 and 900, a client would generate a random number between 1 and 1000. If the result is 1 through 100, the first endpoint would be used. If the result is 101 through 1000, the second endpoint would be used. The same load balancing would be achieved if the weight values were 10 and 90 because weights are relative.", -1)
const _hoisted_22 = /*#__PURE__*/_createElementVNode("p", null, "The same value can be used for all weights if the service owner wants equal load balancing. A value of 1 is typical in this case.", -1)
const _hoisted_23 = /*#__PURE__*/_createElementVNode("h3", null, "Service names", -1)
const _hoisted_24 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Many applications have used SRV records over the years. Service names were first collected in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "http://www.dns-sd.org/servicetypes.html",
    rel: "noopener noreferrer"
  }, "a temporary registry operated by dns-sd.org"),
  /*#__PURE__*/_createTextVNode(". The Internet Assigned Numbers Authority (IANA) now maintains "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://www.iana.org/assignments/service-names-port-numbers/service-names-port-numbers.xhtml",
    rel: "noopener noreferrer"
  }, "an official registry of service names"),
  /*#__PURE__*/_createTextVNode(". The IANA registry includes whether TCP or UDP is supported and a default port number.")
], -1)
const _hoisted_25 = /*#__PURE__*/_createElementVNode("h3", null, "Finding SRV records for a service", -1)
const _hoisted_26 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("To find the set of endpoints for a service that uses SRV records, a client sends a DNS query of type SRV for a query name in the format "),
  /*#__PURE__*/_createElementVNode("code", null, "_Service._Protocol.Name"),
  /*#__PURE__*/_createTextVNode(". The application protocol specifies the service and protocol values in the query name. For Minecraft, the first label is \"_minecraft\" and the second label is \"_tcp\".")
], -1)
const _hoisted_27 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("Putting these values together, the query name to discover the example.org Minecraft servers would be "),
  /*#__PURE__*/_createElementVNode("code", null, "_minecraft._tcp.example.org"),
  /*#__PURE__*/_createTextVNode(".")
], -1)
const _hoisted_28 = /*#__PURE__*/_createElementVNode("h2", null, "In-depth with a Minecraft SRV example", -1)
const _hoisted_29 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createTextVNode("The SRV record set for the Minecraft servers for "),
  /*#__PURE__*/_createElementVNode("code", null, "example.org"),
  /*#__PURE__*/_createTextVNode(" might look like this:")
], -1)
const _hoisted_30 = /*#__PURE__*/_createElementVNode("pre", null, "_minecraft._tcp.example.org 3600 SRV 1 100 25565 mserver1.example.org.\n_minecraft._tcp.example.org 3600 SRV 1  50 25565 mserver2.example.org.\n_minecraft._tcp.example.org 3600 SRV 2 100 25565 mserver3.example.org.", -1)
const _hoisted_31 = /*#__PURE__*/_createElementVNode("p", null, "As we've already seen, the service name is \"_minecraft\", and the protocol is \"_tcp\". The TTL for each record is one hour (3600 seconds).", -1)
const _hoisted_32 = /*#__PURE__*/_createElementVNode("p", null, "The first two endpoints have a priority of 1. Clients contact these first. The third endpoint has a priority of 2 so clients only use it if the first two servers are down. \"mserver3\" is a fallback server.", -1)
const _hoisted_33 = /*#__PURE__*/_createElementVNode("p", null, "The weight for the first server is 100, and the weight for the second server is 50. Clients randomly select between these two servers such that two-thirds of requests go to \"mserver1\" and one-third go to \"mserver2\".", -1)
const _hoisted_34 = /*#__PURE__*/_createElementVNode("p", null, "Lastly comes the port and target. The target is the DNS name of the server hosting the endpoint. This example uses the default Minecraft port of 25565. A different port value can be used. The Minecraft server's port configuration must match the port value in the SRV record.", -1)
const _hoisted_35 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", null, [
    /*#__PURE__*/_createTextVNode("Kubernetes uses SRV records for named ports. A named port is an endpoint for a service. The owner name of the SRV record in the DNS is of the form: "),
    /*#__PURE__*/_createElementVNode("code", null, "_my-port-name._my-port-protocol.my-svc.my-namespace.svc.my-zone"),
    /*#__PURE__*/_createTextVNode(".")
  ])
], -1)
const _hoisted_36 = /*#__PURE__*/_createElementVNode("p", null, "An example SRV record for a Kubernetes HTTPS endpoint for example.org:", -1)
const _hoisted_37 = /*#__PURE__*/_createElementVNode("pre", null, "_https._tcp.kubernetes.default.svc.example.org. 3600 SRV 1 100 443 kubernetes.default.svc.example.org.", -1)
const _hoisted_38 = /*#__PURE__*/_createElementVNode("h2", null, "Other SRV examples", -1)
const _hoisted_39 = /*#__PURE__*/_createElementVNode("p", null, "Many application protocols use SRV records. A few examples:", -1)
const _hoisted_40 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "SIP (Session Initiation Protocol)"),
  /*#__PURE__*/_createTextVNode(": SIP uses a typical SRV record set with _sip as the service name. SIP can use either TCP or UDP.")
], -1)
const _hoisted_41 = /*#__PURE__*/_createElementVNode("pre", null, "_sip._udp.example.org 3600 SRV 1 100 5060 sip_server.example.org.", -1)
const _hoisted_42 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "XMPP (Extensible Messaging and Presence Protocol)"),
  /*#__PURE__*/_createTextVNode(": XMPP generally has two SRV record sets with different service names. \"_xmpp-client\" is used for client-to-server connections. \"_xmpp-server\" is used for server-to-server connections.")
], -1)
const _hoisted_43 = /*#__PURE__*/_createElementVNode("pre", null, "_xmpp-client._tcp.example.org 3600 SRV 1 100 5060 xmpp_server.example.org.\n_xmpp-server._tcp.example.org 3600 SRV 1 100 5060 xmpp_server.example.org.", -1)
const _hoisted_44 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "Kerberos"),
  /*#__PURE__*/_createTextVNode(": Several different SRV records can be defined for locating Kerberos KDCs (Key Distribution Centers). Kerberos supports both TCP and UDP so there will usually be SRV records for both protocols. A SRV for the master KDC can be included. Clients use the \"_kpasswd\" SRV records when changing passwords.")
], -1)
const _hoisted_45 = /*#__PURE__*/_createElementVNode("pre", null, "_kerberos._udp.example.org        3600 SRV 1 100 88  kdc1.example.org.\n_kerberos._tcp.example.org        3600 SRV 1 100 88  kdc1.example.org.\n_kerberos-master._tcp.example.org 3600 SRV 1 100 749 kdcm.example.org.\n_kpasswd._tcp.example.org         3600 SRV 1 100 464 kdc1.example.org.", -1)
const _hoisted_46 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "LDAP (Lightweight Direction Access Protocol)"),
  /*#__PURE__*/_createTextVNode(": The service name \"_ldap\" is used, and only TCP is supported.")
], -1)
const _hoisted_47 = /*#__PURE__*/_createElementVNode("pre", null, "_ldap._tcp.example.org 3600 SRV 1 100 389 ldap1.example.org.\n_ldap._tcp.example.org 3600 SRV 1 100 389 ldap2.example.org.", -1)
const _hoisted_48 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "CalDAV/CardDAV (calendar and address book protocols)"),
  /*#__PURE__*/_createTextVNode(": CalDAV and CardDAV each have two possible SRV record sets: one appended by \"s\" for SSL connections and one without \"s\" for non-SSL connections.")
], -1)
const _hoisted_49 = /*#__PURE__*/_createElementVNode("pre", null, "_caldav._tcp.example.org   3600 SRV 1 100 8008 cal_srv.example.org.\n_caldavs._tcp.example.org  3600 SRV 1 100 8443 cal_secure_srv.example.org.\n_carddav._tcp.example.org  3600 SRV 1 100 8008 card_srv.example.org.\n_carddavs._tcp.example.org 3600 SRV 1 100 8443 card_secure_srv.example.org.", -1)
const _hoisted_50 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "matrix (an open standard real-time communication protocol)"),
  /*#__PURE__*/_createTextVNode(": If no SRV exists, the matrix protocol defines alternative DNS lookups used to locate servers.")
], -1)
const _hoisted_51 = /*#__PURE__*/_createElementVNode("pre", null, "_matrix._tcp.example.org 3600 SRV 1 100 8448 matrix_server.example.org.", -1)
const _hoisted_52 = /*#__PURE__*/_createElementVNode("h2", null, "Other service location record types", -1)
const _hoisted_53 = /*#__PURE__*/_createElementVNode("p", null, "Before SRV, the only way to advertise a service was to publish a set of address records for a name. There was no way to provide a port number or order the endpoints.", -1)
const _hoisted_54 = /*#__PURE__*/_createElementVNode("p", null, "Several other DNS record types exist for service location. These include:", -1)
const _hoisted_55 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "SVCB and HTTPS"),
  /*#__PURE__*/_createTextVNode(": The Service Binding (SVCB) record type is a new standard emerging to address the shortcomings of SRV. HTTPS is a variant of SVCB specifically for HTTP clients. The "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/draft-ietf-dnsop-svcb-https-10",
    rel: "noopener noreferrer"
  }, "SVCB-HTTPS draft"),
  /*#__PURE__*/_createTextVNode(" specifies these types."),
  /*#__PURE__*/_createElementVNode("br"),
  /*#__PURE__*/_createTextVNode("These records are a better version of SRV. They are extensible, can instruct clients to upgrade or switch protocols, and in general improve upon SRV. Even though they are new, they are becoming de facto standard. They are already used by iOS, Chrome, and Firefox.")
], -1)
const _hoisted_56 = /*#__PURE__*/_createElementVNode("strong", null, "MX", -1)
const _hoisted_57 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "URI"),
  /*#__PURE__*/_createTextVNode(": Introduced in "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc7553",
    rel: "noopener noreferrer"
  }, "RFC 7553"),
  /*#__PURE__*/_createTextVNode(", the URI record type was intended to improve on SRV by storing URIs in the DNS instead of hostnames. This record type is not in widespread use.")
], -1)
const _hoisted_58 = /*#__PURE__*/_createElementVNode("li", null, [
  /*#__PURE__*/_createElementVNode("strong", null, "AFSDB"),
  /*#__PURE__*/_createTextVNode(": This is a DNS record type used to publish servers for AFS (the Andrew File System). "),
  /*#__PURE__*/_createElementVNode("a", {
    href: "https://datatracker.ietf.org/doc/html/rfc1183",
    rel: "noopener noreferrer"
  }, "RFC 1183"),
  /*#__PURE__*/_createTextVNode(" specifies AFSDB. It does not include a way to order endpoints.")
], -1)
const _hoisted_59 = /*#__PURE__*/_createElementVNode("h2", null, "Looking up SRV records for a name", -1)
const _hoisted_60 = /*#__PURE__*/_createElementVNode("p", null, "The dig or nslookup command line tools can query SRV records for a DNS name. To find the LDAP SRV records for Google, use this command:", -1)
const _hoisted_61 = /*#__PURE__*/_createElementVNode("pre", null, "dig SRV _ldap._tcp.google.com", -1)
const _hoisted_62 = /*#__PURE__*/_createElementVNode("p", null, "On operating systems that support nslookup, you can use the following:", -1)
const _hoisted_63 = /*#__PURE__*/_createElementVNode("pre", null, "nslookup -type=srv _ldap._tcp.google.com", -1)
const _hoisted_64 = {
  class: "not-prose text-base mx-auto max-w-lg",
  autocomplete: "off"
}
const _hoisted_65 = { class: "mx-auto max-w-3xl divide-y-2 divide-gray-200" }
const _hoisted_66 = /*#__PURE__*/_createElementVNode("h2", { class: "ignore-toc" }, "Related questions", -1)
const _hoisted_67 = { class: "mt-6 space-y-6 divide-y divide-gray-200 [overflow-anchor:none]" }
const _hoisted_68 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " What TTL should you use for SRV records? ", -1)
const _hoisted_69 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_70 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "A TTL of one hour or more is recommended for SRV records because they tend to be relatively static.")
], -1)
const _hoisted_71 = /*#__PURE__*/_createElementVNode("p", null, "SRV record sets can include backup servers with higher priority values. It's not necessary to update the SRV record set when the primary servers are down. Clients will automatically switch to backup servers with no interruption in service. So planning ahead and including backup servers in the SRV record set allows the use of longer TTLs.", -1)
const _hoisted_72 = /*#__PURE__*/_createElementVNode("span", { class: "text-lg font-medium text-gray-900" }, " When should you create SRV records? ", -1)
const _hoisted_73 = { class: "ml-6 flex h-7 items-center" }
const _hoisted_74 = /*#__PURE__*/_createElementVNode("p", null, [
  /*#__PURE__*/_createElementVNode("strong", { class: "font-medium text-gray-500" }, "Create SRV records when you deploy servers for an application protocol that supports SRV.")
], -1)
const _hoisted_75 = /*#__PURE__*/_createElementVNode("p", null, "Not all application protocols use SRV records. Minecraft uses SRV records so when deploying a Minecraft server, a SRV record is needed. However, web clients do not use SRV. When deploying a new website, no SRV record is needed. But you might create an HTTPS record. Consult the documentation for the service to see if it requires or supports SRV.", -1)

import BlogPost from '@/components/layout/BlogPost.vue';


import { useRouter } from 'vue-router';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue';
import { ChevronDownIcon } from '@heroicons/vue/24/outline';
import { jeff, ruurtjan } from '@/common/authors';
import { BlogMeta } from '@/common/blogMeta';
import DomainSearch from '@/components/DomainSearch.vue';
import CodeBlock from '@/components/micro/CodeBlock.vue';
import DnsRecordTypes from '@/views/learning/DnsRecordTypes.vue';
import { learningBreadcrumb } from '@/model/Breadcrumb';
import Callout from '@/components/layout/CalloutComponent.vue';
import { domainNameToUrlPath } from '@/common/domainName';

export const meta: BlogMeta = {
  tag: 'DNS record types',
  url: '/learning/dns-record-types/srv/',
  breadcrumbs: [learningBreadcrumb, DnsRecordTypes.breadcrumb],
  title: 'The SRV record',
  description: 'The SRV or "service locator" DNS record type enables service discovery in the DNS. SRV records allow services to be advertised on specific ports.',
  overviewDescription: 'The SRV or "service locator" DNS record type enables service discovery in the DNS. SRV records allow services to be advertised on specific ports.',
  overviewImage: '/img/learning/dns-record-types/srv/the-dns-srv-record-type-small.jpg',
  overviewImageAlt: 'Service location in the DNS',
  author: jeff,
  editor: ruurtjan,
  img1024x512: 'https://www.nslookup.io/img/learning/dns-record-types/srv/the-srv-record-social.jpg',
  img1200x630: 'https://www.nslookup.io/img/learning/dns-record-types/srv/the-srv-record-social.jpg',
  written: new Date('2022-09-13T12:41:22.269Z'),
  updated: new Date('2023-03-28T11:58:16.472Z'),
};

const __default__ = {
  meta,
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'RecordTypeSrv',
  setup(__props) {

const router = useRouter();

function redirectTo(domain: string): void {
  router.push(`/domains/${domainNameToUrlPath(domain)}/dns-records/srv/`);
}

return (_ctx: any,_cache: any) => {
  const _component_router_link = _resolveComponent("router-link")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (_openBlock(), _createBlock(_Teleport, { to: "head" }, [
      (_openBlock(), _createBlock(_resolveDynamicComponent('script'), { type: "application/ld+json" }, {
        default: _withCtx(() => [
          _createTextVNode(" { \"@context\": \"https://schema.org\", \"@type\": \"ImageObject\", \"author\": { \"@type\": \"Person\", \"email\": \"mailto:ruurtjan@nslookup.io\", \"image\": \"https://www.nslookup.io/img/ruurtjan-lg.jpg\", \"jobTitle\": \"CEO\", \"name\": \"Ruurtjan Pul\", \"url\": \"https://www.nslookup.io/about/ruurtjan-pul/\" }, \"copyrightHolder\": { \"@type\": \"Organization\", \"url\": \"https://www.nslookup.io/\", \"email\": \"hello@nslookup.io\" }, \"copyrightYear\": 2023, \"contentUrl\": \"https://www.nslookup.io/img/learning/dns-record-types/srv/the-dns-srv-record-type.jpg\", \"license\": \"https://creativecommons.org/licenses/by/4.0/\", \"acquireLicensePage\": \"https://creativecommons.org/licenses/by/4.0/\" } ")
        ]),
        _: 1
      }))
    ])),
    _createVNode(BlogPost, {
      title: meta.title,
      description: meta.description,
      url: `https://www.nslookup.io${meta.url}`,
      breadcrumbs: meta.breadcrumbs,
      img1024x512: meta.img1024x512,
      img1200x630: meta.img1200x630,
      written: meta.written,
      updated: meta.updated,
      author: meta.author,
      editor: meta.editor
    }, {
      default: _withCtx(() => [
        _hoisted_1,
        _hoisted_2,
        _hoisted_3,
        _hoisted_4,
        _hoisted_5,
        _hoisted_6,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_7
          ]),
          _: 1
        }),
        _hoisted_8,
        _createElementVNode("ul", null, [
          _hoisted_9,
          _hoisted_10,
          _hoisted_11,
          _hoisted_12,
          _hoisted_13,
          _hoisted_14,
          _hoisted_15,
          _createElementVNode("li", null, [
            _hoisted_16,
            _createTextVNode(": The DNS hostname for this endpoint. The target may resolve to a "),
            _createVNode(_component_router_link, { to: "/learning/dns-record-types/cname/" }, {
              default: _withCtx(() => [
                _createTextVNode("CNAME record")
              ]),
              _: 1
            }),
            _createTextVNode(".")
          ])
        ]),
        _hoisted_17,
        _createElementVNode("p", null, [
          _createTextVNode("Priority and weight determine how clients select one of the endpoints in a set of SRV records. Priority is similar to the preference field in "),
          _createVNode(_component_router_link, { to: "/learning/dns-record-types/mx/" }, {
            default: _withCtx(() => [
              _createTextVNode("MX records")
            ]),
            _: 1
          }),
          _createTextVNode(". But SRV also includes a random component through the weight field for more dynamic load balancing.")
        ]),
        _hoisted_18,
        _hoisted_19,
        _hoisted_20,
        _hoisted_21,
        _hoisted_22,
        _hoisted_23,
        _hoisted_24,
        _hoisted_25,
        _hoisted_26,
        _hoisted_27,
        _hoisted_28,
        _hoisted_29,
        _createVNode(CodeBlock, null, {
          default: _withCtx(() => [
            _hoisted_30
          ]),
          _: 1
        }),
        _hoisted_31,
        _hoisted_32,
        _hoisted_33,
        _hoisted_34,
        _createVNode(Callout, {
          icon: "info",
          title: "SRV records in Kubernetes",
          heading: "h2"
        }, {
          default: _withCtx(() => [
            _hoisted_35,
            _hoisted_36,
            _createVNode(CodeBlock, null, {
              default: _withCtx(() => [
                _hoisted_37
              ]),
              _: 1
            }),
            _createElementVNode("p", null, [
              _createTextVNode("For more information on how Kubernetes uses the DNS, read our article on "),
              _createVNode(_component_router_link, { to: "/learning/the-life-of-a-dns-query-in-kubernetes/" }, {
                default: _withCtx(() => [
                  _createTextVNode("the life of a DNS query in Kubernetes")
                ]),
                _: 1
              }),
              _createTextVNode(".")
            ])
          ]),
          _: 1
        }),
        _hoisted_38,
        _hoisted_39,
        _createElementVNode("ul", null, [
          _hoisted_40,
          _createVNode(CodeBlock, null, {
            default: _withCtx(() => [
              _hoisted_41
            ]),
            _: 1
          }),
          _hoisted_42,
          _createVNode(CodeBlock, null, {
            default: _withCtx(() => [
              _hoisted_43
            ]),
            _: 1
          }),
          _hoisted_44,
          _createVNode(CodeBlock, null, {
            default: _withCtx(() => [
              _hoisted_45
            ]),
            _: 1
          }),
          _hoisted_46,
          _createVNode(CodeBlock, null, {
            default: _withCtx(() => [
              _hoisted_47
            ]),
            _: 1
          }),
          _hoisted_48,
          _createVNode(CodeBlock, null, {
            default: _withCtx(() => [
              _hoisted_49
            ]),
            _: 1
          }),
          _hoisted_50,
          _createVNode(CodeBlock, null, {
            default: _withCtx(() => [
              _hoisted_51
            ]),
            _: 1
          })
        ]),
        _hoisted_52,
        _hoisted_53,
        _hoisted_54,
        _createElementVNode("ul", null, [
          _hoisted_55,
          _createElementVNode("li", null, [
            _hoisted_56,
            _createTextVNode(": The "),
            _createVNode(_component_router_link, { to: "/learning/dns-record-types/mx/" }, {
              default: _withCtx(() => [
                _createTextVNode("Mail Exchange (MX) record type")
              ]),
              _: 1
            }),
            _createTextVNode(" was part of the original DNS specifications. It includes a Preference field to order mail servers. Virtually all email transactions continue to use MX records.")
          ]),
          _hoisted_57,
          _hoisted_58
        ]),
        _hoisted_59,
        _hoisted_60,
        _hoisted_61,
        _hoisted_62,
        _hoisted_63,
        _createElementVNode("p", null, [
          _createTextVNode("You can also check the SRV records for any domain name in our "),
          _createVNode(_component_router_link, { to: "/srv-lookup/" }, {
            default: _withCtx(() => [
              _createTextVNode("SRV lookup")
            ]),
            _: 1
          }),
          _createTextVNode(" tool or by entering it here:")
        ]),
        _createElementVNode("form", _hoisted_64, [
          _createVNode(DomainSearch, {
            emphasize: "",
            buttonText: "Find SRV record",
            onSubmitted: redirectTo
          })
        ]),
        _createElementVNode("div", _hoisted_65, [
          _hoisted_66,
          _createElementVNode("div", _hoisted_67, [
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_68,
                    _createElementVNode("span", _hoisted_69, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_70,
                    _createElementVNode("p", null, [
                      _createTextVNode("TTL selection in the DNS is often complicated. As we discussed in "),
                      _createVNode(_component_router_link, { to: "/learning/what-is-a-good-ttl-for-dns/" }, {
                        default: _withCtx(() => [
                          _createTextVNode("our article on TTL selection in the DNS")
                        ]),
                        _: 1
                      }),
                      _createTextVNode(", shorter TTLs give agility, but longer TTLs increase reliability. Longer TTLs can also decrease latency.")
                    ]),
                    _hoisted_71
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(Disclosure), {
              as: "div",
              class: "pt-6"
            }, {
              default: _withCtx(({ open }) => [
                _createVNode(_unref(DisclosureButton), {
                  as: "h3",
                  class: "ignore-toc flex mt-0 w-full items-start justify-between text-left cursor-pointer text-gray-400"
                }, {
                  default: _withCtx(() => [
                    _hoisted_72,
                    _createElementVNode("span", _hoisted_73, [
                      _createVNode(_unref(ChevronDownIcon), {
                        class: _normalizeClass([open ? '-rotate-180' : 'rotate-0', 'h-6 w-6 transform']),
                        "aria-hidden": "true"
                      }, null, 8, ["class"])
                    ])
                  ]),
                  _: 2
                }, 1024),
                _createVNode(_unref(DisclosurePanel), { class: "mt-2 pr-12" }, {
                  default: _withCtx(() => [
                    _hoisted_74,
                    _hoisted_75
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ])
        ])
      ]),
      _: 1
    }, 8, ["title", "description", "url", "breadcrumbs", "img1024x512", "img1200x630", "written", "updated", "author", "editor"])
  ], 64))
}
}

})